import { rpx } from 'client/lib/rpx-client';
import { URLS } from 'shared/urls';
import { ProvisioningForm } from '@course/components/checkout';
import { SetPasswordForm } from '@course/components/checkout/set-password-form';
import { CourseCheckoutPage } from '@course/components/course-checkout';
import { useGiftState } from './gift-form';
import { useIntl } from 'shared/intl/use-intl';
import { useState } from 'preact/hooks';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defStudentRoute } from '@course/components/courses-app-router';
import { useCurrentTenant, useCurrentUser } from 'client/lib/auth';
import { communityHomeUrl } from 'client/utils/url';
import { isMiraseeCheckout } from 'server/types/pmts-overrides';
import { MiraseeWelcomePage } from '@course/components/sales-checkout/mirasee-welcome-page';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { useRouter } from '@components/router';
import { showError } from '@components/app-error';
import { useOntraportAccountOverride } from 'client/lib/hooks/use-ontraport-account-override';

async function load(route: RouteLoadProps) {
  const searchParams = new URLSearchParams(location.search);

  // Handle redirect return from PayPal / redirect-based payment methods.
  // Stripe appends payment_intent + redirect_status to the return_url.
  const paymentIntent = searchParams.get('payment_intent');
  const setupIntent = searchParams.get('setup_intent');
  const redirectStatus = searchParams.get('redirect_status');
  if ((paymentIntent || setupIntent) && redirectStatus === 'succeeded') {
    const priceId = searchParams.get('priceId');
    if (priceId) {
      try {
        await rpx.courseCheckout.purchase({
          courseId: route.params.courseId,
          priceId,
        });
      } catch (err) {
        console.error('[Provision] Failed to finalize redirect payment:', err);
      }
    }
  }

  return rpx.courseCheckout.getProvisioningState({
    courseId: route.params.courseId,
    priceId: searchParams.get('priceId') || undefined,
  });
}

type Props = LoadedProps<typeof load>;

export const route = defStudentRoute({
  load,
  Page,
  hideChat: true,
  // The thank-you page for a checkout pass holder: it tells them the purchase
  // went to the account and a sign-in link is on its way.
  allowCheckoutPass: true,
});

function fireOntraportPixel(opts: {
  trackingDomain: string;
  courseId: string;
  courseTitle: string;
  purchaseId: string;
  priceInCents: number;
  email: string;
  ontraportProductId?: string;
}) {
  if (!opts.trackingDomain) {
    return;
  }

  const priceDollars = (opts.priceInCents / 100).toFixed(2);
  const params: Record<string, string> = {
    order_id: opts.purchaseId,
    email: opts.email,
    item_id_1: opts.ontraportProductId || opts.courseId,
    item_external_id_1: opts.courseId,
    item_price_1: priceDollars,
    item_qty_1: '1',
  };

  console.info('[Ontraport Tracking] Purchase pixel fired:', {
    ...params,
    item_name: opts.courseTitle,
  });

  const domain = opts.trackingDomain.replace(/^https?:\/\//, '');
  const img = new Image();
  img.src = `https://${domain}/p?${new URLSearchParams(params).toString()}`;
}

function fireMetaPurchasePixel(opts: {
  purchaseId: string;
  priceInCents: number;
  courseId: string;
  courseTitle: string;
}) {
  const fbq = (window as { fbq?: (...args: unknown[]) => void }).fbq;
  if (typeof fbq !== 'function') {
    return;
  }

  const dedupeKey = `meta_purchase_fired_${opts.purchaseId}`;
  if (sessionStorage.getItem(dedupeKey)) {
    return;
  }
  sessionStorage.setItem(dedupeKey, '1');

  const value = opts.priceInCents / 100;
  fbq(
    'track',
    'Purchase',
    {
      value,
      currency: 'USD',
      content_ids: [opts.courseId],
      content_name: opts.courseTitle,
      content_type: 'product',
    },
    { eventID: `purchase_${opts.purchaseId}` },
  );

  console.info('[Meta Tracking] Purchase event fired:', { purchaseId: opts.purchaseId, value });
}

function Page(props: Props) {
  const intl = useIntl();
  const tenant = useCurrentTenant();
  const user = useCurrentUser();
  const {
    course,
    purchase,
    ontraportProductId,
    ontraportAccountId,
    ontraportTrackingDomain,
    redirectUrl,
    upsellId,
    checkoutPageConfig,
    isAipMember,
    hasPassword,
    aipPrice,
    deferredDate,
    deferredUpsellDate,
    deferredUpsellCourseTitle,
    campaignBrand,
    checkoutPass,
  } = props.state;
  useOntraportAccountOverride(ontraportAccountId);
  const [giftState, setGiftState] = useGiftState(course);
  const giftEmailScheduledAt =
    giftState?.emailScheduledAt instanceof Date
      ? giftState.emailScheduledAt.toISOString()
      : giftState?.emailScheduledAt;
  const isGift = props.params.gift === 'true' || !!giftState?.email;
  const isNewUser = props.params.newuser === 'true' && !hasPassword;
  const fromUpsell = props.params.fromUpsell === 'true';
  const upsellAccepted = props.params.upsellAccepted === 'true';

  const doProvision = async () => {
    return rpx.courseCheckout.provision({
      courseId: course.id,
      purchaseId: purchase.id,
      giftMessage: giftState?.message,
      giftShouldSendEmail: giftState?.shouldSendEmail,
      giftEmailScheduledAt,
    });
  };

  const doProvisionAndTrack = async () => {
    const result = await doProvision();

    if (!('isDeferred' in result)) {
      try {
        fireOntraportPixel({
          trackingDomain: ontraportTrackingDomain || tenant.ontraportTrackingDomain || '',
          courseId: course.id,
          courseTitle: course.title,
          purchaseId: purchase.id,
          priceInCents: purchase.priceInCents || 0,
          email: user?.email || checkoutPass?.email || '',
          ontraportProductId,
        });
        sessionStorage.removeItem('affiliate_tracking');
      } catch {
        // tracking failure should never block provisioning
      }
    }

    return result;
  };

  useAsyncEffect(async () => {
    if (fromUpsell) {
      return;
    }
    const paymentCollected =
      purchase.status === 'provisioning' ||
      purchase.status === 'active' ||
      purchase.status === 'complete';
    if (!paymentCollected) {
      return;
    }
    try {
      fireMetaPurchasePixel({
        purchaseId: purchase.id,
        priceInCents: purchase.priceInCents || 0,
        courseId: course.id,
        courseTitle: course.title,
      });
    } catch {
      // tracking failure should never block provisioning
    }
  }, []);

  const hasWelcomePage =
    !isGift &&
    checkoutPageConfig &&
    !Array.isArray(checkoutPageConfig) &&
    isMiraseeCheckout(checkoutPageConfig) &&
    checkoutPageConfig.welcomePage;

  if (hasWelcomePage && (fromUpsell || !upsellId)) {
    return (
      <MiraseeWelcomePage
        config={checkoutPageConfig}
        welcomeConfig={checkoutPageConfig.welcomePage!}
        courseTitle={course.title}
        courseId={course.id}
        priceId={purchase.priceId}
        isNewUser={isNewUser}
        checkoutPass={checkoutPass}
        isAipMember={isAipMember || upsellAccepted}
        upsellAccepted={upsellAccepted}
        brand={campaignBrand}
        aipPrice={fromUpsell ? undefined : aipPrice}
        deferredDate={deferredDate}
        deferredUpsellDate={fromUpsell ? deferredUpsellDate : undefined}
        deferredUpsellCourseTitle={fromUpsell ? deferredUpsellCourseTitle : undefined}
        skipProvisioning={fromUpsell}
        provision={
          fromUpsell
            ? async () => {}
            : async () => {
                await doProvisionAndTrack();
              }
        }
        onAipPurchaseComplete={
          !fromUpsell && aipPrice
            ? async () => {
                props.router.goto('/community');
              }
            : undefined
        }
      />
    );
  }

  if (upsellId && !fromUpsell && !isGift) {
    return (
      <UpsellProvisioningOverlay
        provision={doProvisionAndTrack}
        priceId={purchase.priceId}
        isNewUser={isNewUser}
      />
    );
  }

  const provision = async () => {
    const result = fromUpsell ? await doProvision() : await doProvisionAndTrack();

    if ('giftId' in result && result.giftId) {
      setGiftState(undefined);
      return URLS.student.coursePage({
        course,
        page: `gifts/${result.giftId}/success?emailSent=${!!giftState?.shouldSendEmail}&emailDate=${
          giftEmailScheduledAt || ''
        }`,
      });
    }
    if (redirectUrl) {
      if (/^https?:\/\//.test(redirectUrl)) {
        window.location.assign(redirectUrl);
        return '';
      }
      return redirectUrl;
    }
    // `isAipMember` here is the upsell flag from the checkout endpoint, which
    // is hardcoded true for every non-Mirasee checkout — not a membership
    // answer. communityHomeUrl decides on the tenant first, so a custom-domain
    // buyer never lands in Mirasee's community shell.
    return communityHomeUrl({ isAipMember, user, tenant });
  };

  if (deferredDate && !isGift) {
    return (
      <CourseCheckoutPage course={course} title={intl('Thank you!')}>
        <DeferredProvisioningForm
          provision={async () => {
            fromUpsell ? await doProvision() : await doProvisionAndTrack();
          }}
          courseTitle={course.title}
          deferredDate={deferredDate}
          showPasswordPrompt={isNewUser}
          checkoutPass={checkoutPass}
        />
      </CourseCheckoutPage>
    );
  }

  return (
    <CourseCheckoutPage
      course={course}
      title={course.isProduct || isGift ? intl('Thank you!') : intl('Thank you for joining!')}
    >
      <ProvisioningForm
        purchase={purchase}
        provision={provision}
        showPasswordPrompt={isNewUser && (!upsellId || fromUpsell)}
        checkoutPass={checkoutPass}
      />
    </CourseCheckoutPage>
  );
}

/**
 * What a checkout pass holder sees once the purchase is done: it went to the
 * account for that email, and the owner has been emailed a sign-in link. They
 * are not signed in, so there is nowhere to send them but the login page.
 */
export function CheckoutPassDone(props: { email: string; courseTitle: string }) {
  return (
    <div class="flex flex-col gap-4 text-center">
      <p class="text-lg font-semibold text-gray-900">You're all set!</p>
      <p class="text-gray-600">
        <strong>{props.courseTitle}</strong> has been added to the account for{' '}
        <strong>{props.email}</strong>. We've emailed a sign-in link to that address.
      </p>
      <p class="text-sm text-gray-500">
        Already know the password?{' '}
        <a href="/login" class="text-indigo-600 hover:underline">
          Sign in
        </a>
      </p>
    </div>
  );
}

function UpsellProvisioningOverlay(props: {
  provision(): Promise<unknown>;
  priceId: string;
  isNewUser: boolean;
}) {
  const router = useRouter();
  const [error, setError] = useState(false);

  useAsyncEffect(async () => {
    try {
      await props.provision();
      const returnUrl = `${location.pathname}?priceId=${props.priceId}&newuser=${props.isNewUser}&fromUpsell=true`;
      try {
        sessionStorage.setItem('postUpsellReturn', returnUrl);
      } catch {
        // sessionStorage unavailable
      }
      await router.redirectTo(`/upsell?priceId=${props.priceId}`);
    } catch (err) {
      showError(err);
      setError(true);
    }
  }, []);

  if (error) {
    return (
      <div class="fixed inset-0 bg-white flex items-center justify-center z-50">
        <p class="text-gray-600">Something went wrong. Please refresh the page to try again.</p>
      </div>
    );
  }

  return (
    <div class="fixed inset-0 bg-white flex items-center justify-center z-50">
      <div class="flex flex-col items-center gap-3">
        <div class="w-8 h-8 border-3 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
        <p class="text-gray-500 text-sm">One moment...</p>
      </div>
    </div>
  );
}

function DeferredProvisioningForm(props: {
  provision(): Promise<void>;
  courseTitle: string;
  deferredDate: string;
  showPasswordPrompt?: boolean;
  checkoutPass?: { email: string };
}) {
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useAsyncEffect(async () => {
    try {
      await props.provision();
      if (props.showPasswordPrompt) {
        setShowPassword(true);
      } else {
        setDone(true);
      }
    } catch (err) {
      showError(err);
      setError(true);
    }
  }, []);

  if (error) {
    return (
      <div class="flex flex-col gap-4 text-center">
        <p class="text-gray-600">Something went wrong. Please refresh the page to try again.</p>
      </div>
    );
  }

  if (showPassword) {
    return (
      <SetPasswordForm onComplete={async () => setDone(true)} onSkip={async () => setDone(true)} />
    );
  }

  if (done) {
    const formattedDate = new Date(
      props.deferredDate.slice(0, 10) + 'T00:00:00',
    ).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return (
      <div class="flex flex-col gap-4 text-center">
        <p class="text-lg font-semibold text-gray-900">
          Your enrollment in {props.courseTitle} begins {formattedDate}.
        </p>
        <p class="text-sm text-gray-600">We'll send you an email when your access is ready.</p>
        {props.checkoutPass && (
          <p class="text-sm text-gray-600">
            It's on the account for <strong>{props.checkoutPass.email}</strong>. We've emailed a
            sign-in link to that address.
          </p>
        )}
      </div>
    );
  }

  return (
    <div class="flex flex-col gap-4">
      <p>Please wait while we finish setting things up...</p>
      <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div class="h-full bg-indigo-600 rounded-full animate-pulse" style="width: 60%" />
      </div>
    </div>
  );
}
